import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { TransportFormData, TransportLeg } from "./transportTypes";
import { DollarSign, ArrowRight, Plane, Bus, Train } from "lucide-react";
import useCurrencyStore from "@/stores/ui/currencyStore";
import { getFlagImage } from "@/lib/general";

interface CostSelectorProps {
  formData: TransportFormData;
  currentLegIndex: number;
  handleInputChange: (field: string, value: any) => void;
  setCurrentLegIndex: (index: number) => void;
}

const CostSelector: React.FC<CostSelectorProps> = ({
  formData,
  currentLegIndex,
  handleInputChange,
  setCurrentLegIndex,
}) => {
  const [costType, setCostType] = useState<"total" | "per-leg">("total");
  const [totalCostInput, setTotalCostInput] = useState("");
  const { selectedCurrency } = useCurrencyStore();

  const calculateTotalCost = () => {
    return formData.legs.reduce((sum, leg) => sum + (Number(leg.cost) || 0), 0);
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleTotalCostChange = (value: string) => {
    setTotalCostInput(value);

    if (value === "") {
      const newLegs = formData.legs.map((leg) => ({
        ...leg,
        cost: null,
      }));
      handleInputChange("legs", newLegs);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const costPerLeg = numValue / Number(formData.numberOfLegs);
        const newLegs = formData.legs.map((leg) => ({
          ...leg,
          cost: costPerLeg,
        }));
        handleInputChange("legs", newLegs);
      }
    }
  };

  const handlePerLegCostChange = (value: string) => {
    const currentLeg = formData.legs[currentLegIndex];
    if (!currentLeg) return;

    const newLegs = [...formData.legs];
    if (value === "") {
      newLegs[currentLegIndex] = {
        ...currentLeg,
        cost: 0,
      };
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        newLegs[currentLegIndex] = {
          ...currentLeg,
          cost: numValue,
        };
      }
    }
    handleInputChange("legs", newLegs);
  };

  const getTransportIcon = (mode: TransportLeg["mode"]) => {
    switch (mode) {
      case "flight":
        return <Plane className="w-3 h-3" />;
      case "bus":
        return <Bus className="w-3 h-3" />;
      case "train":
        return <Train className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const renderLegInfo = (leg: TransportLeg, index: number) => {
    const hasLocation = leg.origin || leg.destination;
    return (
      <div className="flex items-center gap-1.5">
        <span className="flex items-center justify-center w-5 h-5 bg-neutral-100 rounded-full text-[10px] text-neutral-600">
          {index + 1}
        </span>
        {hasLocation ? (
          <div className="flex items-center gap-1.5 text-xs">
            <span className="">From</span>
            <span className=" font-medium">{leg.origin || "?"}</span>
            <span className="">to</span>
            <span className=" font-medium">{leg.destination || "?"}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-neutral-600">
            <span>Leg {index + 1}</span>
            {leg.mode && (
              <>
                <span className="text-neutral-300">•</span>
                <span className="flex items-center gap-1">
                  {getTransportIcon(leg.mode)}
                  {leg.mode.charAt(0).toUpperCase() + leg.mode.slice(1)}
                </span>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCurrencyInput = (
    value: string,
    onChange: (value: string) => void,
    placeholder: string
  ) => (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm font-light">
        {selectedCurrency.symbol}
      </span>
      <Input
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm focus-visible:outline-none focus-visible:ring-0 rounded-lg pl-8 h-9"
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-1 bg-neutral-100 p-1 rounded-lg">
          <button
            onClick={() => {
              setCostType("total");
              setTotalCostInput(calculateTotalCost().toString());
            }}
            className={`py-1.5 px-3 rounded-md flex items-center gap-1.5 transition-all text-xs ${
              costType === "total"
                ? "bg-white shadow-sm text-neutral-800"
                : "text-neutral-600 hover:text-neutral-800"
            }`}
          >
            Total Cost
          </button>
          {formData.legs.length > 1 && (
            <button
              onClick={() => setCostType("per-leg")}
              className={`py-1.5 px-3 rounded-md flex items-center gap-1.5 transition-all text-xs ${
                costType === "per-leg"
                  ? "bg-white shadow-sm text-neutral-800"
                  : "text-neutral-600 hover:text-neutral-800"
              }`}
            >
              Per Leg
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <span>Enter in {selectedCurrency.code}</span>
          <img
            src={getFlagImage(selectedCurrency.country)}
            alt={selectedCurrency.country}
            className="w-4 h-4 rounded-full"
          />
        </div>
      </div>

      <div className="h-full">
        {costType === "total" ? (
          <div className="space-y-2">
            {renderCurrencyInput(
              totalCostInput,
              handleTotalCostChange,
              "Enter total trip cost"
            )}
            {formData.numberOfLegs && Number(formData.numberOfLegs) > 1 && (
              <div className="flex justify-between items-center px-3 py-2 bg-neutral-50 rounded-lg text-xs">
                <span className="text-neutral-500">Average cost per leg</span>
                <span className="font-medium">
                  {selectedCurrency.symbol}
                  {formatCurrency(
                    parseFloat(totalCostInput) /
                      Number(formData.numberOfLegs) || 0
                  )}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="p-3 rounded-lg border border-neutral-200">
              <div className="flex flex-col gap-2 mb-3">
                {formData.legs[currentLegIndex] &&
                  renderLegInfo(
                    formData.legs[currentLegIndex],
                    currentLegIndex
                  )}
                {formData.legs[currentLegIndex]?.provider && (
                  <div className="text-xs text-neutral-500 ml-6">
                    with {formData.legs[currentLegIndex].providerName}
                  </div>
                )}
              </div>

              {renderCurrencyInput(
                formData.legs[currentLegIndex]?.cost?.toString() || "",
                handlePerLegCostChange,
                "Enter cost for this leg"
              )}

              {formData.legs.length > 1 && (
                <div className="mt-3 border-t pt-3 border-neutral-100">
                  <div className="text-xs text-neutral-500 mb-2">All legs:</div>
                  <div className="flex flex-wrap gap-2">
                    {formData.legs.map((leg, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentLegIndex(index)}
                        className={`flex items-center gap-1.5 py-1.5 px-2 rounded-lg text-xs transition-all ${
                          index === currentLegIndex
                            ? "bg-indigo-600 text-white"
                            : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                        }`}
                      >
                        {renderLegInfo(leg, index)}
                        {leg.cost !== undefined && leg.cost !== null && (
                          <span
                            className={`ml-1 ${
                              index === currentLegIndex
                                ? "text-indigo-100"
                                : "text-neutral-500"
                            }`}
                          >
                            • {selectedCurrency.symbol}
                            {formatCurrency(leg.cost)}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostSelector;
